/* eslint @typescript-eslint/no-explicit-any: "off" */
import React, { useCallback, useEffect, useState } from "react";
import { I18n } from "@aws-amplify/core";
import { Auth } from "aws-amplify";
import { Box, Card, Grid, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import { TotpEnrolment } from "../components/TotpEnrolment";
import { BasePage } from "./BasePage";

/**
 * Self-service two-factor enrolment for a signed-in user.
 *
 * This page is what makes a staged MFA rollout possible. While the user pool is
 * set to OPTIONAL, Cognito never issues an MFA_SETUP challenge, so there is no
 * other route to enrol -- without this, the only way to get anyone onto MFA would
 * be a hard cutover to REQUIRED for every admin and agent at once.
 *
 * It stays useful afterwards for re-enrolling on a replacement phone. A user who
 * has *lost* their device cannot reach this page (they cannot complete sign-in)
 * and needs an administrator to clear their MFA preference from the CLI -- see
 * docs/runbooks/mfa-reset.md.
 *
 * Enrolling here does not require a policy-compliant password. The two are
 * independent in Cognito, so a user still on an older, shorter password can
 * enrol today and change their password separately.
 */
export const SecurityPage: BasePage = () => {
  const [user, setUser] = useState<any>(null);
  const [enrolled, setEnrolled] = useState<boolean | null>(null);
  const [justEnrolled, setJustEnrolled] = useState(false);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(() => {
    Auth.currentAuthenticatedUser({ bypassCache: true })
      .then((current: any) => {
        setUser(current);
        return Auth.getPreferredMFA(current, { bypassCache: true });
      })
      .then((preferred: string) => {
        // "NOMFA" when nothing is registered; "SOFTWARE_TOKEN_MFA" once enrolled.
        setEnrolled(preferred === "SOFTWARE_TOKEN_MFA");
      })
      .catch(() =>
        setLoadError(
          I18n.get(
            "Could not read your two-factor status. Please reload the page."
          )
        )
      );
  }, []);

  useEffect(load, [load]);

  const onComplete = useCallback(() => {
    setJustEnrolled(true);
    setEnrolled(true);
  }, []);

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8} lg={6}>
          <Card variant="outlined">
            <Box p={3} display="flex" flexDirection="column">
              <Typography
                style={{ fontWeight: "bold", fontSize: "large" }}
                gutterBottom
              >
                {I18n.get("Two-factor authentication")}
              </Typography>

              {loadError && (
                <Alert variant="outlined" severity="error">
                  {loadError}
                </Alert>
              )}

              {justEnrolled && (
                <Alert variant="outlined" severity="success">
                  {I18n.get(
                    "Two-factor authentication is now on. You will be asked for a code from your authenticator app each time you sign in."
                  )}
                </Alert>
              )}

              {!loadError && enrolled === null && (
                <Typography>{I18n.get("Checking…")}</Typography>
              )}

              {enrolled === true && !justEnrolled && (
                <>
                  <Alert variant="outlined" severity="success">
                    {I18n.get(
                      "Two-factor authentication is on for your account."
                    )}
                  </Alert>
                  <Box mt={2}>
                    <Typography variant="body2">
                      {I18n.get(
                        "Replacing your phone? Set the new device up below — the old one stops working as soon as the new code is verified. If you have already lost the device, an administrator must reset it for you."
                      )}
                    </Typography>
                  </Box>
                  <Box mt={2}>
                    {user && (
                      <TotpEnrolment
                        user={user}
                        onComplete={onComplete}
                        submitLabel={I18n.get("Replace authenticator")}
                      />
                    )}
                  </Box>
                </>
              )}

              {enrolled === false && (
                <>
                  <Typography gutterBottom>
                    {I18n.get(
                      "Protect your account with a code from your phone in addition to your password. This will become mandatory — setting it up now means nothing changes for you when it does."
                    )}
                  </Typography>
                  <Box mt={2}>
                    {user && (
                      <TotpEnrolment user={user} onComplete={onComplete} />
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};
